import { call, put, takeLatest } from 'redux-saga/effects';
import { TAction } from '../actions/types/action';
import { apiServiceAsync } from '../services/apiService';
import { LOCATION_CHANGE } from '../../../../../shared/frontend/libs/router/redux-store';
import { stripPathLocale } from '../../../../../shared/common/localeShared';

//
// Actions
//

type TMessageCenterClientState = {};

export type TActions = {
  setMessageCenterUnreadRequested(): TAction<any>;
  setMessageCenterUnreadSucceeded(amount: number): TAction<any>;
  setMessageCenterUnreadFailed(error: any): TAction<any>;

  setListMessageCenterThreadRequested(): TAction<any>;
  setListMessageCenterThreadSucceeded(results: any): TAction<any>;
  setListMessageCenterThreadFailed(error: any): TAction<any>;

  setFormContent(postContent: any): TAction<any>;
  setBlockModalIsOpen(isOpen: boolean): TAction<any>;
  setMessageCenterThreadRead(threadId: number): TAction<any>;
};

export const actionTypes = {
  SET_MESSAGE_CENTER_UNREAD_REQUESTED: 'MESSAGE_CENTER/GLOBAL_UNREAD_REQUESTED',
  SET_MESSAGE_CENTER_UNREAD_SUCCEEDED: 'MESSAGE_CENTER/GLOBAL_UNREAD_SUCCEEDED',
  SET_MESSAGE_CENTER_UNREAD_FAILED: 'MESSAGE_CENTER/GLOBAL_UNREAD_FAILED',

  SET_LIST_MESSAGE_CENTER_THREAD_REQUESTED: 'MESSAGE_CENTER/LIST_THREAD_REQUESTED',
  SET_LIST_MESSAGE_CENTER_THREAD_SUCCEEDED: 'MESSAGE_CENTER/LIST_THREAD_SUCCEEDED',
  SET_LIST_MESSAGE_CENTER_THREAD_FAILED: 'MESSAGE_CENTER/LIST_THREAD_FAILED',

  SET_FORM_CONTENT: 'MESSAGE_CENTER/SET_FORM_CONTENT',

  SET_BLOCK_MODAL_IS_OPEN: 'MESSAGE_CENTER/SET_BLOCK_MODAL_IS_OPEN',
  SET_THREAD_READ: 'MESSAGE_CENTER/SET_THREAD_READ'
};

export const actions: TActions = {
  //
  // Unread (Globa Navbar)
  //
  setMessageCenterUnreadRequested() {
    return { type: actionTypes.SET_MESSAGE_CENTER_UNREAD_REQUESTED, payload: {} };
  },

  setMessageCenterUnreadSucceeded(amount: number) {
    return { type: actionTypes.SET_MESSAGE_CENTER_UNREAD_SUCCEEDED, payload: { amount } };
  },

  setMessageCenterUnreadFailed(error: any) {
    return { type: actionTypes.SET_MESSAGE_CENTER_UNREAD_FAILED, payload: error };
  },

  //
  // List Thread (Nav)
  //
  setListMessageCenterThreadRequested() {
    return { type: actionTypes.SET_LIST_MESSAGE_CENTER_THREAD_REQUESTED, payload: {} };
  },

  setListMessageCenterThreadSucceeded(results: any) {
    return { type: actionTypes.SET_LIST_MESSAGE_CENTER_THREAD_SUCCEEDED, payload: { results } };
  },

  setListMessageCenterThreadFailed(error: any) {
    return { type: actionTypes.SET_LIST_MESSAGE_CENTER_THREAD_FAILED, payload: error };
  },

  //
  // Handle Form
  //
  setFormContent(postContent: any) {
    return { type: actionTypes.SET_FORM_CONTENT, payload: postContent };
  },

  //
  // Block Modal
  //
  setBlockModalIsOpen(isOpen: boolean) {
    return { type: actionTypes.SET_BLOCK_MODAL_IS_OPEN, payload: isOpen };
  },

  //
  // Thread
  //
  setMessageCenterThreadRead(threadId: number) {
    return { type: actionTypes.SET_THREAD_READ, payload: threadId };
  }
};

//
// Reducer
//

function getInitialState() {
  return {
    listThread: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false,
      threads: []
    },
    unreadAmount: {
      requestsSinceChangeCount: 0,
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },
    form: {
      content: ''
    },
    blockModal: {
      isOpen: false
    }
  };
}

export function reducer(state: any = getInitialState(), action: TAction<any>): Partial<TMessageCenterClientState> {
  switch (action.type) {
    case actionTypes.SET_LIST_MESSAGE_CENTER_THREAD_REQUESTED: {
      return {
        ...state,
        listThread: {
          ...state.listThread,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_LIST_MESSAGE_CENTER_THREAD_SUCCEEDED: {
      return {
        ...state,
        listThread: {
          ...state.listThread,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          ...action.payload.results
        }
      };
    }

    case actionTypes.SET_LIST_MESSAGE_CENTER_THREAD_FAILED: {
      return {
        ...state,
        listThread: {
          ...state.listThread,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SET_FORM_CONTENT: {
      return {
        ...state,
        form: {
          content: action.payload
        }
      };
    }

    case actionTypes.SET_MESSAGE_CENTER_UNREAD_REQUESTED: {
      return {
        ...state,
        unreadAmount: {
          ...state.unreadAmount,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_MESSAGE_CENTER_UNREAD_SUCCEEDED: {
      return {
        ...state,
        unreadAmount: {
          ...state.unreadAmount,
          requestsSinceChangeCount:
            state.unreadAmount.amount === action.payload.amount ? state.unreadAmount.requestsSinceChangeCount + 1 : 0,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          amount: action.payload.amount
        }
      };
    }

    case actionTypes.SET_MESSAGE_CENTER_UNREAD_FAILED: {
      return {
        ...state,
        unreadAmount: {
          ...state.unreadAmount,
          requestsSinceChangeCount: 0,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SET_BLOCK_MODAL_IS_OPEN: {
      return {
        ...state,
        blockModal: {
          ...state.blockModal,
          isOpen: action.payload
        }
      };
    }

    case actionTypes.SET_THREAD_READ: {
      return {
        ...state,
        listThread: {
          ...state.listThread,
          threads: state.listThread?.threads.map((t: any) => ({
            ...t,
            unread: t.id === action.payload ? 0 : t.unread
          }))
        }
      };
    }

    case LOCATION_CHANGE: {
      const location = stripPathLocale(action.payload.location.pathname);
      if (!location.startsWith('/messages')) {
        return {
          ...getInitialState(),
          unreadAmount: state.unreadAmount
        };
      }
      return state;
    }
  }

  return state;
}

//
// Sagas
//

//
// list thread
//
function* listMyMessage(action: TAction<any>): any {
  try {
    const results = yield call(apiServiceAsync.listMyMessage);
    yield put(actions.setListMessageCenterThreadSucceeded(results));
  } catch (e) {
    yield put(actions.setListMessageCenterThreadFailed(e.toString()));
  }
}

function* watchListMessageCenterThreadRequested(): any {
  yield takeLatest(actionTypes.SET_LIST_MESSAGE_CENTER_THREAD_REQUESTED, listMyMessage);
}

//
// global unread
//
function* fetchUnreadMessages(action: TAction<any>): any {
  try {
    const results = yield call(apiServiceAsync.fetchUnreadAmount);
    yield put(actions.setMessageCenterUnreadSucceeded(results.unread));
  } catch (e) {
    yield put(actions.setMessageCenterUnreadFailed(e.toString()));
  }
}

function* watchMessageCenterUnreadRequested(): any {
  yield takeLatest(actionTypes.SET_MESSAGE_CENTER_UNREAD_REQUESTED, fetchUnreadMessages);
}

export const sagas = [watchListMessageCenterThreadRequested(), watchMessageCenterUnreadRequested()];
